/**
 * TodaySlotGrid.js — vendor-parity Today experience for Lumina scheduler.
 *
 * Renders an OPEN + BOOKED slot grid matching the AriaOne vendor UI:
 *   - top bar: location dropdown, "X open" / "Y booked" counts, Book buttons
 *   - 4-column card grid; each card shows time, state badge, location, and
 *     (for BOOKED) patient + visit-type chip + walk-in pill
 *   - empty state only fires when BOTH slots and appointments are empty
 *
 * All data comes from the parent (`cards`, `summary`, `locations`). This
 * component is intentionally pure-presentational + dropdown-stateful; data
 * fetching lives in EchoScheduler.
 */
import { useMemo } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';
import { CalendarPlus, MapPin, Users, AlertTriangle, RefreshCw, Loader2, Calendar, ChevronLeft, ChevronRight, UserPlus, CheckCircle2, ShieldAlert, DollarSign } from 'lucide-react';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const displayTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// Shift a YYYY-MM-DD string by `days` (accepting negative). Returns the
// same format so querystring + <input type="date"> round-trip cleanly.
const shiftDate = (iso, days) => {
  if (!iso) return new Date().toISOString().slice(0, 10);
  const [y, m, d] = iso.split('-').map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const formatHumanDate = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

/* ─── Card ────────────────────────────────────────────────────────────── */

const SlotCard = ({ card, isFirstAvailable, onBook, onOpen, onCheckin }) => {
  const open = card.kind === 'open';
  // Status-aware visuals for non-open cards. Vendor-canonical statuses
  // map to distinct chip colors so the grid reads at a glance:
  //   booked     → slate (default)
  //   checked_in → blue
  //   in_exam    → violet (active care)
  //   checkout   → amber (ready to leave)
  //   completed  → emerald
  //   no_show    → red
  //   cancelled  → muted slate
  const STATUS_CHIP = {
    booked:     { label: 'Booked',     cls: 'bg-slate-900 text-white' },
    checked_in: { label: 'Checked-in', cls: 'bg-blue-100 text-blue-700' },
    in_exam:    { label: 'In Exam',    cls: 'bg-violet-100 text-violet-700' },
    checkout:   { label: 'Checkout',   cls: 'bg-amber-100 text-amber-700' },
    completed:  { label: 'Completed',  cls: 'bg-emerald-100 text-emerald-700' },
    no_show:    { label: 'No-show',    cls: 'bg-red-100 text-red-700' },
    cancelled:  { label: 'Cancelled',  cls: 'bg-slate-100 text-slate-500 line-through' },
  };
  const cardStatus = open ? 'open' : (card.status || 'booked');
  const chip = open
    ? { label: 'Open', cls: 'bg-emerald-100 text-emerald-700' }
    : (STATUS_CHIP[cardStatus] || { label: String(cardStatus).toUpperCase(), cls: 'bg-slate-100 text-slate-700' });
  // Card body coloring follows the chip: open=emerald-tinted, in_exam=violet-tinted,
  // checkout=amber-tinted, no_show=red-tinted, else white.
  const stateCls = open
    ? 'border-emerald-300 bg-emerald-50/30 hover:bg-emerald-50'
    : cardStatus === 'in_exam'
      ? 'border-violet-200 bg-violet-50/40 hover:bg-violet-50'
      : cardStatus === 'checkout'
        ? 'border-amber-200 bg-amber-50/40 hover:bg-amber-50'
        : cardStatus === 'no_show'
          ? 'border-red-200 bg-red-50/40 hover:bg-red-50'
          : cardStatus === 'completed'
            ? 'border-emerald-200 bg-white hover:bg-emerald-50/40'
            : 'border-slate-300 bg-white hover:bg-slate-50';
  const handleActivate = () => (open ? onBook(card) : onOpen?.(card));
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleActivate(); } }}
      className={cn(
        'group text-left relative overflow-hidden rounded-xl border transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 cursor-pointer',
        stateCls,
        isFirstAvailable && 'ring-2 ring-emerald-500/70',
      )}
      style={{ padding: '14px 14px 12px 14px', minHeight: 110 }}
      data-testid={`slot-card-${card.slotId || card.appointmentId || card.startAt}`}
      data-slot-kind={card.kind}
      data-slot-status={cardStatus}
      data-slot-start={card.startAt || ''}
      data-provider-id={card.providerId || ''}
      data-location-id={card.locationId || ''}
    >
      {isFirstAvailable && (
        <span className="absolute top-0 left-0 text-[9px] font-semibold uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 rounded-br-md" data-testid="first-available-pill">
          First available
        </span>
      )}
      <div className="flex items-start justify-between">
        <div className="flex items-baseline gap-1.5">
          <span className="text-base font-semibold text-slate-900">{displayTime(card.startAt)}</span>
          {card.isWalkIn && (
            <span className="text-[9px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded" data-testid={`walkin-pill-${card.appointmentId || card.startAt}`}>
              Walk-in
            </span>
          )}
        </div>
        <Badge
          className={cn(
            'text-[9px] font-semibold uppercase tracking-wider border-0',
            chip.cls,
          )}
          data-testid={`slot-badge-${cardStatus}`}
        >
          {chip.label}
        </Badge>
      </div>

      {!open && card.patient && (
        <div className="mt-1 flex items-center gap-1.5 min-w-0">
          <p className="text-xs font-medium text-slate-800 truncate flex-1" title={card.patient}>
            {card.patient}
          </p>
          {typeof card.copay === 'number' && card.copay > 0 && (
            <Badge
              variant="outline"
              className="text-[8px] border-amber-200 text-amber-700 bg-amber-50 px-1 py-0 leading-none"
              data-testid={`slot-copay-${card.appointmentId || card.startAt}`}
              title={`Copay due $${card.copay}`}
            >
              <DollarSign className="h-2 w-2 mr-0.5" />{card.copay}
            </Badge>
          )}
          {typeof card.noShow === 'number' && card.noShow >= 30 && (
            <span
              className="text-[9px] font-semibold text-red-600 inline-flex items-center gap-0.5 shrink-0"
              data-testid={`slot-noshow-${card.appointmentId || card.startAt}`}
              title={`No-show risk ${Math.round(card.noShow * (card.noShow > 1 ? 1 : 100))}%`}
            >
              <AlertTriangle className="h-2.5 w-2.5" />
              {Math.round(card.noShow * (card.noShow > 1 ? 1 : 100))}%
            </span>
          )}
        </div>
      )}

      {!open && card.insuranceVerified === false && (
        <div
          className="mt-1 inline-flex items-center gap-1 text-[9px] text-red-600 font-medium"
          data-testid={`slot-ins-unverified-${card.appointmentId || card.startAt}`}
          title="Insurance not verified"
        >
          <ShieldAlert className="h-2.5 w-2.5" /> Insurance unverified
        </div>
      )}

      {!open && card.visitType && (
        <div className="mt-1.5 inline-flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: card.visitColor || '#94a3b8' }}
          />
          <span className="text-[11px] text-slate-600">{card.visitType}</span>
        </div>
      )}

      <div className="mt-2 flex items-center gap-1 text-[10px] text-slate-500">
        <MapPin className="h-3 w-3" />
        <span className="truncate">{card.locationName || '—'}</span>
        {card.providerName && (
          <>
            <span className="mx-0.5">·</span>
            <Users className="h-3 w-3" />
            <span className="truncate">{card.providerName}</span>
          </>
        )}
      </div>

      {!open && onCheckin && (cardStatus === 'booked' || cardStatus === 'checked_in') && card.appointmentId && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCheckin(card); }}
          className="mt-2 w-full h-7 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 text-[10px] font-medium inline-flex items-center justify-center gap-1"
          style={{ cursor: 'pointer' }}
          data-testid={`slot-checkin-btn-${card.appointmentId}`}
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
          {cardStatus === 'checked_in' ? 'In waiting room' : 'Check in'}
        </button>
      )}
    </div>
  );
};

/* ─── Grid ────────────────────────────────────────────────────────────── */

const TodaySlotGrid = ({
  cards = [],
  summary = { open: 0, booked: 0, total: 0 },
  entities = [],
  entityId = '',
  onEntityChange,
  locations = [],
  locationId = '',
  onLocationChange,
  onBook,
  onOpen,
  onCheckin,
  loading = false,
  error = null,
  availabilityError = null,
  onRetry,
  onNewBooking,
  onWalkin,
  allowBooking = true,
  selectedDate = '',
  onDateChange,
}) => {
  const firstOpenIdx = useMemo(() => cards.findIndex((c) => c.kind === 'open'), [cards]);

  return (
    <div className="space-y-4" data-testid="today-slot-grid">
      {/* ── top bar ─────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2">
          <select
            value={entityId || ''}
            onChange={(e) => onEntityChange?.(e.target.value)}
            data-testid="entity-filter"
            className="text-xs h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300 min-w-[170px]"
          >
            <option value="">Select provider…</option>
            {entities.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
          <select
            value={locationId || ''}
            onChange={(e) => onLocationChange?.(e.target.value)}
            data-testid="location-filter"
            className="text-xs h-9 px-3 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <option value="">All Locations</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
          {/* ── date navigation (prev / picker / next / today) ── */}
          <div className="flex items-center gap-1 ml-1">
            <button
              type="button"
              onClick={() => onDateChange?.(shiftDate(selectedDate, -1))}
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              style={{ cursor: 'pointer' }}
              data-testid="date-prev-btn"
              aria-label="Previous day"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange?.(e.target.value)}
              className="text-xs h-9 px-2 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-slate-300"
              data-testid="date-picker"
              aria-label="Pick date"
            />
            <button
              type="button"
              onClick={() => onDateChange?.(shiftDate(selectedDate, 1))}
              className="h-9 w-9 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              style={{ cursor: 'pointer' }}
              data-testid="date-next-btn"
              aria-label="Next day"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onDateChange?.(new Date().toISOString().slice(0, 10))}
              className="h-9 px-2.5 inline-flex items-center text-[11px] font-medium rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              style={{ cursor: 'pointer' }}
              data-testid="date-today-btn"
            >
              Today
            </button>
            <span className="text-[11px] text-slate-500 ml-1 hidden md:inline" data-testid="date-label">
              {formatHumanDate(selectedDate)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          <span className="inline-flex items-center gap-1 text-[11px] font-medium" data-testid="open-count">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            <span className="text-emerald-700">{summary.open}</span>
            <span className="text-slate-400">open</span>
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] font-medium" data-testid="booked-count">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-900" />
            <span className="text-slate-800">{summary.booked}</span>
            <span className="text-slate-400">booked</span>
          </span>
          <Button
            variant="outline"
            onClick={() => onWalkin?.()}
            className="h-9 text-xs gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50"
            data-testid="slot-grid-walkin-btn"
          >
            <UserPlus className="h-3.5 w-3.5" /> Walk-in
          </Button>
          <Button
            onClick={() => onNewBooking?.()}
            className="h-9 text-xs gap-1.5 bg-[#111] hover:bg-[#222] text-white"
            data-testid="slot-grid-book-btn"
          >
            <CalendarPlus className="h-3.5 w-3.5" /> Book
          </Button>
        </div>
      </div>

      {/* ── availability-specific error banner (distinct from primary error) ── */}
      {!loading && !error && availabilityError && (
        <div
          className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2"
          data-testid="availability-error"
          data-error-code={availabilityError.code || 'ERROR'}
          data-error-status={availabilityError.status || 0}
        >
          <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
          <span className="text-[11px] text-amber-800 flex-1">
            {availabilityError.code === 'NO_ENTITY_CONTEXT' || availabilityError.code === 'MISSING_SCOPE'
              ? (availabilityError.message || 'Select provider and location to load availability.')
              : (availabilityError.message || 'Could not load open slot inventory.')}
          </span>
          {onRetry && availabilityError.code !== 'NO_ENTITY_CONTEXT' && availabilityError.code !== 'MISSING_SCOPE' && (
            <button onClick={onRetry} className="text-[11px] text-amber-800 underline inline-flex items-center gap-1" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="availability-retry">
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          )}
        </div>
      )}

      {/* ── loading ─────────────────────────────────────────── */}
      {loading && (
        <div className="py-12 flex items-center justify-center" data-testid="today-grid-loading">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          <span className="ml-2 text-[11px] text-slate-500">Loading today's slots…</span>
        </div>
      )}

      {/* ── error ────────────────────────────────────────────── */}
      {!loading && error && (
        <div
          className="p-3 rounded-lg bg-red-50 border border-red-100 flex items-center gap-2"
          data-testid="today-grid-error"
          data-error-code={error.code || 'ERROR'}
          data-error-status={error.status || 0}
        >
          <AlertTriangle className="h-4 w-4 text-red-500 shrink-0" />
          <span className="text-[11px] text-red-700 flex-1">{error.message || 'Failed to load'}</span>
          {onRetry && (
            <button onClick={onRetry} className="text-[11px] text-red-700 underline inline-flex items-center gap-1" style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="today-grid-retry">
              <RefreshCw className="h-3 w-3" /> Retry
            </button>
          )}
        </div>
      )}

      {/* ── empty state ─────────────────────────────────────── */}
      {!loading && !error && cards.length === 0 && (
        <div className="py-12 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50/30" data-testid="today-grid-empty">
          <Calendar className="h-7 w-7 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-medium text-slate-600">No slots or appointments for today</p>
          <p className="text-[11px] text-slate-400 mt-1">Try a different location or date, or create a new booking.</p>
        </div>
      )}

      {/* ── grid ─────────────────────────────────────────────── */}
      {!loading && !error && cards.length > 0 && (
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}
          data-testid="slot-grid-cards"
        >
          {cards.map((c, i) => (
            <SlotCard
              key={`${c.slotId || c.appointmentId || c.startAt}-${i}`}
              card={c}
              isFirstAvailable={i === firstOpenIdx}
              onBook={onBook}
              onOpen={onOpen}
              onCheckin={onCheckin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TodaySlotGrid;
